import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight, Loader2, Facebook, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { NewsletterBox } from "@/components/NewsletterBox";
import axios from "axios";
import { useLang, Hreflang } from "@/i18n/LanguageContext";
import { blogPath, homePath } from "@/i18n/slugs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const catColors = {
  prix: "bg-blue-100 text-blue-700",
  primes: "bg-green-100 text-green-700",
  conseils: "bg-orange-100 text-orange-700",
};

function renderBold(text, keyPrefix) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) => (i % 2 === 1 ? <strong key={`${keyPrefix}-${i}`}>{part}</strong> : part));
}

export function ArticleContent({ content }) {
  const blocks = content.split(/\n\s*\n/);
  return (
    <div className="space-y-5">
      {blocks.map((block, bi) => {
        const lines = block.trim().split("\n");
        if (lines[0].startsWith("## ")) {
          return (
            <div key={bi} className="space-y-4">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 pt-4">
                {lines[0].replace("## ", "")}
              </h2>
              {lines.length > 1 && (
                <ArticleContent content={lines.slice(1).join("\n")} />
              )}
            </div>
          );
        }
        if (lines.every((l) => l.trim().startsWith("- "))) {
          return (
            <ul key={bi} className="space-y-2 pl-1">
              {lines.map((l, li) => (
                <li key={li} className="flex items-start gap-3 text-slate-600 leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2.5 flex-shrink-0" />
                  <span>{renderBold(l.replace(/^- /, ""), `${bi}-${li}`)}</span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={bi} className="text-slate-600 leading-relaxed">
            {renderBold(block.trim(), `p-${bi}`)}
          </p>
        );
      })}
    </div>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const { lang, T } = useLang();
  const B = T.blog;
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPost(null);
    setError(false);
    axios.get(`${API}/blog/posts/${slug}`).then((res) => setPost(res.data)).catch(() => setError(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-40 pb-32 text-center">
          <p className="text-slate-600 mb-6">Article introuvable / Artikel niet gevonden</p>
          <Link to={blogPath(lang)}><Button variant="outline">{B.back}</Button></Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-40 pb-32 text-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto" /></div>
        <Footer />
      </div>
    );
  }

  const title = lang === "nl" ? post.title_nl : post.title_fr;
  const excerpt = lang === "nl" ? post.excerpt_nl : post.excerpt_fr;
  const content = lang === "nl" ? post.content_nl : post.content_fr;
  const frPath = `/blog/${post.slug}`;
  const nlPath = `/nl/blog/${post.slug_nl}`;
  const canonical = `https://devismaison.be${lang === "nl" ? nlPath : frPath}`;

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <Helmet>
        <title>{`${title} | DevisMaison.be`}</title>
        <meta name="description" content={excerpt} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={excerpt} />
        <meta property="og:url" content={canonical} />
        {post.image_url && <meta property="og:image" content={post.image_url} />}
        <meta property="og:locale" content={lang === "nl" ? "nl_BE" : "fr_BE"} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": title,
          "description": excerpt,
          "image": post.image_url,
          "datePublished": post.created_at,
          "dateModified": post.updated_at,
          "publisher": { "@type": "Organization", "name": "DevisMaison.be", "url": "https://devismaison.be" },
          "mainEntityOfPage": canonical
        })}</script>
      </Helmet>
      <Hreflang frPath={frPath} nlPath={nlPath} />
      <Navbar />

      <article className="pt-24 pb-16 lg:pt-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to={blogPath(lang)} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-orange-500 transition-colors mb-6" data-testid="blog-back-link">
            <ArrowLeft className="w-4 h-4" /> {B.back}
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${catColors[post.category] || catColors.conseils}`}>
              {B.categories[post.category] || post.category}
            </span>
            <span className="text-sm text-slate-400">
              {B.published} {new Date(post.created_at).toLocaleDateString(lang === "nl" ? "nl-BE" : "fr-BE", { day: "numeric", month: "long", year: "numeric" })}
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
            {title}
          </h1>
          {post.image_url && (
            <div className="aspect-[16/9] overflow-hidden rounded-2xl mb-8">
              <img src={post.image_url} alt={title} className="w-full h-full object-cover" />
            </div>
          )}
          <ArticleContent content={content} />

          <div className="flex items-center gap-3 mt-10 pt-6 border-t border-slate-200" data-testid="share-buttons">
            <span className="text-sm font-medium text-slate-600">{B.share}</span>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonical)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-[#1877F2] hover:opacity-85 transition-opacity flex items-center justify-center"
              aria-label="Facebook"
              data-testid="share-facebook"
            >
              <Facebook className="w-4 h-4 text-white" />
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonical)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-[#0A66C2] hover:opacity-85 transition-opacity flex items-center justify-center"
              aria-label="LinkedIn"
              data-testid="share-linkedin"
            >
              <Linkedin className="w-4 h-4 text-white" />
            </a>
          </div>

          <div className="mt-10 p-8 bg-slate-900 rounded-2xl text-center" data-testid="blog-post-cta">
            <h3 className="font-heading text-2xl font-bold text-white mb-2">{B.ctaTitle}</h3>
            <p className="text-slate-300 mb-6">{B.ctaText}</p>
            <Link to={`${homePath(lang)}#lead-form`}>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full px-8">
                {B.ctaBtn}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="mt-8">
            <NewsletterBox />
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
